import liff from '@line/liff'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  getApiV1WeddingsByWeddingIdRsvpsTokenInfo,
  postApiV1WeddingsByWeddingIdRsvps,
  postApiV1WeddingsByWeddingIdRsvpsPublic,
} from '@wedding-tech/api-client'
import {
  getApiV1WeddingsByWeddingIdAuthMeOptions,
  getApiV1WeddingsByWeddingIdSettingsOptions,
} from '@wedding-tech/api-client/tanstack'
import { Badge } from '@wedding-tech/ui/badge'
import { Button } from '@wedding-tech/ui/button'
import { Input } from '@wedding-tech/ui/input'
import { Textarea } from '@wedding-tech/ui/textarea'
import confetti from 'canvas-confetti'
import {
  Calendar,
  CheckCircle2,
  Heart,
  Loader2,
  User,
  Users,
  XCircle,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import { formatThaiDate } from '@/lib/date'
import { useLIFF } from '@/lib/liff/context'

const rsvpSearchSchema = z.object({
  token: z.string().optional(),
})

export const Route = createFileRoute('/e/$eventId/rsvp')({
  validateSearch: rsvpSearchSchema,
  component: LIFFRSVPPage,
})

function extractErrorMessage(err: unknown, fallback: string): string {
  const e = err as any
  if (typeof e?.body === 'string' && e.body) return e.body
  if (e?.body?.error && typeof e.body.error === 'string') return e.body.error
  if (e?.error && typeof e.error === 'string') return e.error
  if (e?.message && typeof e.message === 'string') return e.message
  return fallback
}

function LIFFRSVPPage() {
  const { eventId } = Route.useParams()
  const { token } = Route.useSearch()
  const { profile, isInClient } = useLIFF()

  const [attending, setAttending] = useState(true)
  const [partySize, setPartySize] = useState(1)
  const [fullName, setFullName] = useState('')
  const [notes, setNotes] = useState('')
  const [submittedData, setSubmittedData] = useState<{
    attending: boolean
    partySize: number
    fullName: string
    notes?: string
  } | null>(null)

  // Fetch wedding settings (public link enabled status & date range)
  const settingsQuery = useQuery(
    getApiV1WeddingsByWeddingIdSettingsOptions({
      path: { weddingId: eventId },
    }),
  )

  // Fetch caller profile and preloaded guest info via LINE auth
  const authMeQuery = useQuery({
    ...getApiV1WeddingsByWeddingIdAuthMeOptions({
      path: { weddingId: eventId },
    }),
    enabled: Boolean(profile?.userId),
  })

  // Extract Event Title and Event Date Time from settings data
  const rawSettings = settingsQuery.data as any
  const settingsData = (rawSettings?.data ?? rawSettings) || {}
  const eventTitle =
    settingsData.coupleNames ||
    settingsData.title ||
    settingsData.weddingTitle ||
    settingsData.name ||
    ''

  const rawDate =
    settingsData.eventDate ||
    settingsData.weddingDate ||
    settingsData.date ||
    settingsData.dateTime ||
    settingsData.rsvpDateRange?.start

  const eventDateDisplay = rawDate ? formatThaiDate(rawDate) : ''

  // Fetch guest token info if token is present
  const tokenInfoQuery = useQuery({
    queryKey: ['rsvpsTokenInfo', eventId, token],
    queryFn: async () => {
      const res = await getApiV1WeddingsByWeddingIdRsvpsTokenInfo({
        path: { weddingId: eventId },
        query: { token: token! },
      })
      return res.data
    },
    enabled: Boolean(token),
  })

  const preloadedGuest = authMeQuery.data?.preloadedGuest
  const hasCheckedIn = authMeQuery.data?.status?.hasCheckedIn

  // Prefill fields when token data, LINE profile, or /auth/me loads
  useEffect(() => {
    if (tokenInfoQuery.data) {
      const info = tokenInfoQuery.data
      setFullName(info.fullName || info.displayName || '')
      setAttending(info.status !== 'declined')
      setPartySize(info.expectedFollowers || 1)
      setNotes(info.notes || '')
    } else if (preloadedGuest) {
      setFullName(
        (prev) =>
          prev || preloadedGuest.fullName || preloadedGuest.displayName || '',
      )
      setAttending(preloadedGuest.status !== 'declined')
      setPartySize(preloadedGuest.expectedFollowers || 1)
      setNotes(preloadedGuest.notes || '')
    } else if (profile?.displayName) {
      setFullName((prev) => prev || profile.displayName || '')
    }
  }, [tokenInfoQuery.data, preloadedGuest, profile?.displayName])

  // Unique Token RSVP Mutation
  const tokenRsvpMutation = useMutation({
    mutationFn: async () => {
      if (!token) throw new Error('Missing token')
      const res = await postApiV1WeddingsByWeddingIdRsvps({
        path: { weddingId: eventId },
        body: {
          invitationToken: token,
          attending,
          partySize,
        },
      })
      return res.data
    },
    onSuccess: () => {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      })
      setSubmittedData({
        attending,
        partySize,
        fullName: fullName || tokenInfoQuery.data?.fullName || 'ผู้ร่วมงาน',
        notes,
      })
      toast.success('บันทึกการตอบรับคำเชิญเรียบร้อยแล้ว')
    },
    onError: (err: unknown) => {
      toast.error(extractErrorMessage(err, 'ไม่สามารถส่งการตอบรับคำเชิญได้'))
    },
  })

  // Public RSVP Mutation
  const publicRsvpMutation = useMutation({
    mutationFn: async () => {
      if (!fullName.trim()) throw new Error('กรุณากรอกชื่อ-นามสกุล')
      const res = await postApiV1WeddingsByWeddingIdRsvpsPublic({
        path: { weddingId: eventId },
        body: {
          fullName: fullName.trim(),
          expectedFollowers: partySize,
          notes: notes.trim() || undefined,
        },
      })
      return res.data
    },
    onSuccess: () => {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      })
      setSubmittedData({
        attending: true,
        partySize,
        fullName: fullName.trim(),
        notes: notes.trim(),
      })
      toast.success('ลงทะเบียนตอบรับคำเชิญเรียบร้อยแล้ว')
    },
    onError: (err: unknown) => {
      toast.error(extractErrorMessage(err, 'ไม่สามารถลงทะเบียนได้'))
    },
  })

  const isPublicEnabled = settingsData.isPublicRsvpEnabled ?? true

  const isSubmitting =
    tokenRsvpMutation.isPending || publicRsvpMutation.isPending

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (token) {
      tokenRsvpMutation.mutate()
    } else {
      publicRsvpMutation.mutate()
    }
  }

  const isLineAccountLocked = Boolean(
    hasCheckedIn ||
    (preloadedGuest &&
      (preloadedGuest.status === 'confirmed' ||
        preloadedGuest.status === 'declined' ||
        Boolean(preloadedGuest.rsvpAt))),
  )

  const isExistingSubmitted = Boolean(
    (token &&
      tokenInfoQuery.data &&
      (tokenInfoQuery.data.status === 'confirmed' ||
        tokenInfoQuery.data.status === 'declined')) ||
    isLineAccountLocked,
  )

  const activeSubmittedData =
    submittedData ||
    (isExistingSubmitted
      ? {
          attending:
            tokenInfoQuery.data?.status === 'confirmed'
              ? true
              : tokenInfoQuery.data?.status === 'declined'
                ? false
                : preloadedGuest?.status === 'declined'
                  ? false
                  : true,
          partySize:
            tokenInfoQuery.data?.expectedFollowers ||
            preloadedGuest?.expectedFollowers ||
            1,
          fullName:
            tokenInfoQuery.data?.fullName ||
            tokenInfoQuery.data?.displayName ||
            preloadedGuest?.fullName ||
            preloadedGuest?.displayName ||
            profile?.displayName ||
            'ผู้ร่วมงาน',
          notes:
            tokenInfoQuery.data?.notes || preloadedGuest?.notes || notes || '',
        }
      : null)

  // Loading state
  if (
    settingsQuery.isLoading ||
    (token && tokenInfoQuery.isLoading) ||
    (profile?.userId && authMeQuery.isLoading)
  ) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center p-4">
        <Loader2 className="text-primary size-8 animate-spin" />
        <p className="text-muted-foreground mt-2 text-sm">กำลังโหลดข้อมูล...</p>
      </div>
    )
  }

  // Closed / Public Disabled State
  if (!token && !isPublicEnabled) {
    return (
      <div className="relative mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center p-6 text-center">
        <div className="space-y-4">
          <XCircle className="text-destructive mx-auto size-16" />
          <h1 className="text-2xl font-bold">ปิดการลงทะเบียนสาธารณะ</h1>
          <p className="text-muted-foreground text-sm">
            การลงทะเบียนตอบรับคำเชิญผ่านลิงก์สาธารณะปิดแล้ว
            หากท่านมีลิงก์เฉพาะบุคคล กรุณาเปิดผ่านลิงก์ของท่าน
          </p>
          {isInClient && (
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="w-full font-semibold"
              onClick={() => liff.closeWindow()}
            >
              ปิดหน้าต่าง
            </Button>
          )}
        </div>
      </div>
    )
  }

  // Submitted / Success / Already Responded State
  if (activeSubmittedData) {
    return (
      <div className="relative mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center space-y-6 p-6 text-center">
        <div className="bg-primary/10 text-primary mx-auto flex size-20 items-center justify-center rounded-full">
          <CheckCircle2 className="size-12" />
        </div>

        <div className="space-y-2">
          {eventTitle && (
            <Badge
              variant="outline"
              className="text-primary border-primary/30 px-3 py-1 text-xs font-semibold"
            >
              {eventTitle}
            </Badge>
          )}
          <h1 className="text-2xl font-bold">
            {isExistingSubmitted && !submittedData
              ? 'คุณได้ตอบรับคำเชิญเรียบร้อยแล้ว'
              : 'ตอบรับคำเชิญเรียบร้อยแล้ว'}
          </h1>
          {eventDateDisplay && (
            <p className="text-muted-foreground flex items-center justify-center gap-1.5 pt-0.5 text-xs">
              <Calendar className="size-3.5" />
              <span>{eventDateDisplay}</span>
            </p>
          )}
          <p className="text-muted-foreground pt-1 text-sm">
            {isExistingSubmitted && !submittedData
              ? 'ท่านได้ตอบรับคำเชิญนี้แล้ว หากต้องการเปลี่ยนแปลงข้อมูล กรุณาติดต่อผู้จัดงาน'
              : 'ขอบคุณสำหรับข้อมูลการตอบรับคำเชิญ'}
          </p>
        </div>

        <div className="bg-card space-y-3 rounded-2xl border p-5 text-left text-sm shadow-sm">
          <div className="flex items-center justify-between border-b pb-3">
            <span className="text-muted-foreground">ชื่อผู้ตอบรับ:</span>
            <span className="font-semibold">
              {activeSubmittedData.fullName}
            </span>
          </div>
          <div className="flex items-center justify-between border-b pb-3">
            <span className="text-muted-foreground">การเข้าร่วม:</span>
            <Badge
              variant={activeSubmittedData.attending ? 'default' : 'outline'}
            >
              {activeSubmittedData.attending
                ? 'เข้าร่วมงาน'
                : 'ไม่สามารถเข้าร่วม'}
            </Badge>
          </div>
          {activeSubmittedData.attending && (
            <div className="flex items-center justify-between border-b pb-3">
              <span className="text-muted-foreground">จำนวนผู้ร่วมงาน:</span>
              <span className="font-semibold">
                {activeSubmittedData.partySize} ท่าน
              </span>
            </div>
          )}
          {activeSubmittedData.notes && (
            <div className="space-y-1 pt-1">
              <span className="text-muted-foreground block text-xs">
                ข้อความถึงคู่บ่าวสาว / หมายเหตุ:
              </span>
              <p className="text-foreground italic">
                {activeSubmittedData.notes}
              </p>
            </div>
          )}
        </div>

        {isInClient && (
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="w-full font-semibold"
            onClick={() => liff.closeWindow()}
          >
            ปิดหน้าต่าง
          </Button>
        )}
      </div>
    )
  }

  // RSVP Form UI (Full page max-w-md centered layout)
  return (
    <div className="bg-background relative mx-auto flex min-h-dvh w-full max-w-md flex-col justify-between p-6">
      <div className="space-y-6 pt-4 pb-8">
        <div className="space-y-3 text-center">
          <div className="bg-primary/10 text-primary mx-auto flex size-14 items-center justify-center rounded-full">
            <Heart className="size-7 fill-current" />
          </div>

          <div className="space-y-1.5">
            {eventTitle && (
              <Badge
                variant="outline"
                className="text-primary border-primary/30 px-3 py-1 text-xs font-semibold"
              >
                {eventTitle}
              </Badge>
            )}
            <h1 className="text-foreground text-2xl font-bold tracking-tight">
              ตอบรับคำเชิญ (RSVP)
            </h1>
            {eventDateDisplay && (
              <p className="text-muted-foreground flex items-center justify-center gap-1.5 pt-0.5 text-xs">
                <Calendar className="text-primary size-3.5" />
                <span>{eventDateDisplay}</span>
              </p>
            )}
          </div>

          {tokenInfoQuery.data ? (
            <p className="text-muted-foreground pt-1 text-sm font-medium">
              เรียนคุณ{' '}
              <span className="text-foreground font-semibold">
                {tokenInfoQuery.data.fullName ||
                  tokenInfoQuery.data.displayName}
              </span>
            </p>
          ) : (
            <p className="text-muted-foreground pt-1 text-sm">
              กรุณากรอกข้อมูลการลงทะเบียนตอบรับคำเชิญ
            </p>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-5"
        >
          {/* Public registration full name field */}
          {!token && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <User className="text-muted-foreground size-4" />
                ชื่อ-นามสกุลผู้ตอบรับ{' '}
                <span className="text-destructive">*</span>
              </label>
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="เช่น นายสมชาย เข็มกลัด"
                required
              />
            </div>
          )}

          {/* Attendance Toggle for Token mode */}
          {token && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Calendar className="text-muted-foreground size-4" />
                สถานะการเข้าร่วมงาน
              </label>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant={attending ? 'default' : 'outline'}
                  className="w-full"
                  onClick={() => setAttending(true)}
                >
                  เข้าร่วมงาน
                </Button>
                <Button
                  type="button"
                  variant={!attending ? 'destructive' : 'outline'}
                  className="w-full"
                  onClick={() => setAttending(false)}
                >
                  ไม่สามารถเข้าร่วม
                </Button>
              </div>
            </div>
          )}

          {/* Party Size Counter (if attending) */}
          {(attending || !token) && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Users className="text-muted-foreground size-4" />
                จำนวนผู้ร่วมงาน (รวมตัวท่าน)
              </label>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={partySize <= 1}
                  onClick={() => setPartySize((p) => Math.max(1, p - 1))}
                >
                  -
                </Button>
                <span className="w-12 text-center text-lg font-bold">
                  {partySize}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setPartySize((p) => Math.min(20, p + 1))}
                >
                  +
                </Button>
                <span className="text-muted-foreground text-sm">ท่าน</span>
              </div>
            </div>
          )}

          {/* Notes / Message to Couple */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              ข้อความถึงคู่บ่าวสาว / หมายเหตุ (ข้อจำกัดอาหาร)
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="เช่น แพ้อาหารทะเล / ยินดีด้วยนะครับ"
              rows={3}
            />
          </div>

          <Button
            type="submit"
            size="lg"
            className="w-full font-bold"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                กำลังบันทึก...
              </>
            ) : (
              'ยืนยันการตอบรับคำเชิญ'
            )}
          </Button>
        </form>
      </div>
    </div>
  )
}
